import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useApiClient } from "../../contexts/ApiClientContext";
import { useAuth } from "../../contexts/AuthContext";
import { useAlerts } from "../../contexts/AlertsContext";
import APIEndPoints from "../../utils/APIEndPoints";
import AlertMessageType from "../common/alert_message/AlertMessageType";
import SETTINGS, { formatDateZeroTime } from "../../globals";
import AppSections from "../../utils/AppSections";
import { IApiClientError } from "../../shared/ApiClient";
import APIError from "../../utils/APIError";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import DialogWindowType from "../common/dialog_window/DialogWindowType";
import Loading from "../common/Loading";
import FormContainer from "../common/FormContainer";
import { FaInfoCircle } from "react-icons/fa";
import FormInputText from "../common/FormInputText";
import FormTextArea from "../common/FormTextArea";
import FormInputImage from "../common/FormInputImage";
import FormInputFile from "../common/FormInputFile";
import AppSubSections from "../../utils/AppSubSections";
import DocumentCategory from "../../model/enums/DocumentCategory";
import AnnexData from "../../model/AnnexData";
import FormDropDown from "../common/FormDropDown";
import UploadManager from "../../shared/UploadManager";
import UploadFileType from "../../model/enums/UploadFileType";
import ImgUtil from "../../utils/ImgUtil";
import UploadManagerProgressReport from "../../model/UploadManagerProgressReport";
import { EXPLANATION_HEADER } from "../common/dialog_window/LicenseDialog";

interface IAnnexDataFormErrors {
  title: string;
  content: string;
  category: string;
}

const defaultFormErrors: IAnnexDataFormErrors = {
  title: "",
  content: "",
  category: "",
};

function AnnexDataAddEdit() {
  // read the ID from the url
  const { id, chid, sid, e3did } = useParams();

  // manage loading animation
  const [loading, setLoading] = useState(true);
  // manage uploading & creating animations
  const [uploading, setUploading] = useState(false);
  // manage upload progress display
  const [uploadProgress, setUploadProgress] = useState<
    UploadManagerProgressReport | undefined
  >(undefined);
  // set the state to track any error messages received from the server
  const [errorMessage, setErrorMessage] = useState("");
  // set state for form info
  const [formData, setFormData] = useState<AnnexData>(
    AnnexData.EmptyInstance(),
  );
  // set state for form errors
  const [formErrors, setFormErrors] =
    useState<IAnnexDataFormErrors>(defaultFormErrors);

  // reference to the ApiClient instance
  const apiClient = useApiClient();
  // get a reference to navigation component
  const navigate = useNavigate();
  // reference to the AuthManager
  const authManager = useAuth();
  // reference to the alerts system
  const alertsManager = useAlerts();

  const [agreeTerms, setAgreeTerms] = useState<boolean>(false);

  // instance of UploadManager
  const updateUploadProgress = (info: UploadManagerProgressReport) => {
    setUploadProgress(info);
  };
  const [uploadManager] = useState(
    new UploadManager({
      apiClient: apiClient,
      progressCallback: updateUploadProgress,
    }),
  );

  // load record data from the server
  useEffect(() => {
    const getAllData = async () => {
      try {
        let annexData: AnnexData | undefined = undefined;
        if (id) {
          annexData = await apiClient?.get<AnnexData>({
            url: APIEndPoints.ANNEXDATA.ONE(parseInt(id)),
          });
        }

        // unify initial model with data from all sources
        const currentData = annexData ? annexData : AnnexData.EmptyInstance();
        if (chid) {
          currentData.id_chelement = parseInt(chid);
        }

        if (sid) {
          currentData.id_site = parseInt(sid);
        }

        if (e3did) {
          currentData.id_e3dmodel = parseInt(e3did);
        }

        // set into the state all the information
        setFormData(currentData);
        setFormErrors(defaultFormErrors);
      } catch (error: any) {
        if (!error.processed) {
          setErrorMessage("Something went wrong. Try again later!");
        }
      }
      setLoading(false);
    };
    getAllData();
  }, [apiClient, id, chid, sid, e3did]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // validate errors from the form
    const errors = validateSiteForm(formData);

    if (isFormInvalid(errors)) {
      setFormErrors(errors);
      setErrorMessage(
        "Some of the data input is invalid. Please check and try again.",
      );
      return;
    }

    setUploading(true);
    var iconServerId: number | undefined = id ? formData.icon : undefined;
    try {
      // upload the new icon, if it exists (situation managed by the UploadManager)
      const uploadResult = await uploadManager.uploadFiles();
      console.log(uploadResult);
      if (uploadResult instanceof Array) {
        iconServerId = uploadResult[0].serverId;
      }
    } catch (exception) {
      console.log(exception);
    }

    var documentFilesServerIds: Array<number> = [];
    try {
      // upload the files
      const uploadResult = await uploadManager.uploadFiles();
      console.log(uploadResult);
      if (uploadResult instanceof Array) {
        uploadResult.forEach((result) => {
          if (result.identifier === "docs" && result.serverId) {
            documentFilesServerIds.push(result.serverId);
          }
        });
      }
    } catch (exception) {
      console.log(exception);
    }

    var body = new AnnexData(
      authManager.getUserID()!!,
      formData.id_chelement,
      formData.id_e3dmodel,
      formData.id_site,
      formData.title,
      formData.content,
      iconServerId,
      formatDateZeroTime(new Date()), // format date as YYYY-MM-DD
      formData.category,
      documentFilesServerIds ? documentFilesServerIds : undefined,
    );

    // check if we have EDIT or CREATE
    if (id) {
      body.id = parseInt(id!);
      apiClient
        ?.put({
          url: APIEndPoints.ANNEXDATA.ONE(body.id),
          body: body,
        })
        .then((response) => {
          setFormErrors(defaultFormErrors); // Reset errors
          alertsManager.showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: "Document updated successfully",
          });
          navigate(
            SETTINGS.get_path({
              section: AppSections.ANNEXDATA,
              id: parseInt(id),
              subsection: AppSubSections.VIEW,
            }),
          );
        })
        .catch((error: IApiClientError) => {
          if (!error.processed) {
            processAPIErrors(error);
          }
          setUploading(false);
        });
    } else {
      // check if we are adding document for Site or CHElement
      apiClient
        ?.post<AnnexData>({
          url: APIEndPoints.ANNEXDATA.NEW,
          body: body,
        })
        .then((response) => {
          setFormErrors(defaultFormErrors);
          alertsManager.showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: "Document added successfully",
          });
          navigate(
            SETTINGS.get_path({
              section: AppSections.ANNEXDATA,
              id: response.id,
              subsection: AppSubSections.VIEW,
            }),
          );
        })
        .catch((error: IApiClientError) => {
          if (!error.processed) {
            processAPIErrors(error);
          }
          setUploading(false);
        });
    }
  };

  const handleDelete = () => {
    apiClient
      ?.delete({
        url: APIEndPoints.ANNEXDATA.ONE(parseInt(id!)),
      })
      .then((response) => {
        alertsManager.showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: "Document deleted successfully",
        });
        navigate(
          SETTINGS.go_back({
            section: formData.id_chelement
              ? AppSections.CHELEMENTS
              : AppSections.SITES,
            id: formData.id_chelement
              ? formData.id_chelement
              : formData.id_site,
            subsection: AppSubSections.VIEW,
          }),
        );
      })
      .catch((error: IApiClientError) => {
        if (!error.processed) {
          alertsManager.showAlertMessage({
            type: AlertMessageType.ERROR,
            message: "Unable to delete this document",
          });
        }
      });
  };

  const formInputChange = (inputID: string, newValue: string) => {
    const formDataCopy = JSON.parse(JSON.stringify(formData));
    formDataCopy[inputID] = newValue;
    setFormData(formDataCopy as AnnexData);
  };

  const formIconChange = (
    id: string,
    value: string | undefined,
    files: FileList | null,
  ) => {
    uploadManager.replaceFiles(id, UploadFileType.ICON, files);
    const formDataCopy = JSON.parse(JSON.stringify(formData));
    formDataCopy["icon_path"] = value;
    formDataCopy["icon"] = -1;
    setFormData(formDataCopy as AnnexData);
  };

  const formFileChange = (
    fieldId: string,
    keyForData: string,
    keyForDisplay: string,
    files: FileList | null,
  ) => {
    let uploadFileType: UploadFileType | undefined = undefined;
    let display = "";

    if (fieldId === "docs") {
      uploadFileType = UploadFileType.ANNEX_DATA;
    }
    if (uploadFileType) {
      uploadManager.replaceFiles(fieldId, uploadFileType, files);

      // process the display, based on the name of the files
      if (files) {
        for (let i = 0; i < files.length; i++) {
          display += display === "" ? files[i].name : ", " + files[i].name;
        }
      }
    } else {
      display = "ERROR: Upload file type error! Please contact developer.";
    }
    const formDataCopy = JSON.parse(JSON.stringify(formData));
    formDataCopy[keyForDisplay] = display === "" ? undefined : display;
    formDataCopy[keyForData] = -1;
    setFormData(formDataCopy as AnnexData);
  };

  const onFormCancel = () => {
    navigate(
      SETTINGS.go_back({
        section: formData.id_chelement
          ? AppSections.CHELEMENTS
          : AppSections.SITES,
        id: formData.id_chelement ? formData.id_chelement : formData.id_site,
        subsection: AppSubSections.VIEW,
      }),
    );
  };

  const processAPIErrors = (serverError: IApiClientError) => {
    try {
      // convert the error to ChemiNovaAPI custom errors
      const apiErrors = serverError.details.response.data as APIError[];
      const formErrorsCopy: any = defaultFormErrors;
      console.log(serverError);
      console.log(apiErrors);
      apiErrors.forEach((apiError) => {
        formErrorsCopy[apiError.loc[0]] = apiError.msg;
      });
      setFormErrors(formErrorsCopy);
      setErrorMessage(
        "Some of the data input is invalid. Please check and try again.",
      );
    } catch (exception) {
      console.log(exception);
      setErrorMessage("Something went wrong. Please try again later!");
    }
  };

  const pageTitle = id ? "Edit document" : "Add document";
  const confirmLabel = id ? "Update document" : "Add document";

  const categoryOptions = Object.entries(DocumentCategory).map(
    ([key, value]) => ({
      label: key,
      value: value,
    }),
  );

  const deleteButton = id ? (
    <Button
      label="Delete document"
      purpose={ButtonAction.DELETE}
      onClick={() => {
        alertsManager.showDialogWindow({
          type: DialogWindowType.CONFIRM,
          message: "Are you sure you want to delete this document?",
          cancelLabel: "Cancel",
          confirmLabel: "Yes, delete document",
          onConfirm: handleDelete,
        });
      }}
    />
  ) : null;

  if (loading || uploading) {
    return (
      <Loading
        uploading={uploading}
        uploadingMessage="Please wait. Uploading documents..."
        uploadingProgress={uploadProgress}
      />
    );
  }

  return (
    <FormContainer
      title={pageTitle}
      onSubmit={(e) => {
        handleSubmit(e);
      }}
      headButton={deleteButton}
    >
      <FormInputImage
        label="Picture"
        id="icon"
        placeholderImage=""
        value={ImgUtil.display_icon(formData.icon_path)}
        required={false}
        onChange={formIconChange}
      />

      <FormInputText
        label="Document title"
        id="title"
        value={formData.title}
        required={true}
        placeholder="Document title"
        onChange={formInputChange}
        error={formErrors.title}
      />

      <FormTextArea
        label="Content"
        id="content"
        value={formData.content}
        required={true}
        onChange={formInputChange}
        error={formErrors.content}
      />

      {false && (
        <FormDropDown
          label="Document category"
          id="category"
          value={formData.category}
          required={true}
          options={categoryOptions}
          onChange={formInputChange}
        />
      )}

      {!id && (
        <FormInputFile
          label="Select file(s) to upload"
          id="docs"
          keyForData="docs"
          keyForDisplay="docs_name"
          value={formData.docs_name}
          required={false}
          disabled={!!id}
          multipleFiles={true}
          acceptedFileTypes="*"
          onChange={formFileChange}
        />
      )}

      {/*{id && Array.isArray(formData.docs) && formData.docs.length > 0 && (
        <label className="form-label">Image files:</label>
      )}

      {id &&
        Array.isArray(formData.docs) &&
        formData.docs.map((doc) => {
          return (
            <FormDownloadFile
              key={doc.id}
              file_name={doc.name}
              file_upload_id={doc.id}
            />
          );
        })}*/}

      {errorMessage && (
        <div className="form-submit-error">
          <FaInfoCircle />
          <div>{errorMessage}</div>
        </div>
      )}

      {/* License Agreement group */}
      <div className="form-input-group mt-4 flex flex-col gap-2 pl-4">
        <label className="text-sm">{EXPLANATION_HEADER}</label>

        <label className="flex items-center gap-2 text-sm mt-1">
          <input
            type="checkbox"
            checked={agreeTerms}
            onChange={(e) => setAgreeTerms(e.target.checked)}
          />
          <span>
            I agree with the{" "}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                alertsManager.showDialogWindow({
                  type: DialogWindowType.LICENSE,
                  message: "license",
                });
              }}
              rel="noopener noreferrer"
              className="text-blue-600 underline"
            >
              Terms and Conditions
            </a>
          </span>
        </label>
      </div>

      <div className="form-buttons-container">
        <Button
          label="Cancel"
          purpose={ButtonAction.CANCEL}
          onClick={() => onFormCancel()}
          className="w-full"
        />
        <Button
          label={confirmLabel}
          purpose={ButtonAction.SAVE}
          isSubmit={true}
          disabled={!agreeTerms}
          className="w-full"
        />
      </div>
    </FormContainer>
  );
}

function validateSiteForm(formData: AnnexData): IAnnexDataFormErrors {
  let errors: IAnnexDataFormErrors = { title: "", content: "", category: "" };

  errors.title =
    formData.title.length < 4
      ? "Document title should be at least 4 characters long"
      : "";
  errors.content =
    formData.content.length < 10
      ? "Document content should be at least 10 characters long"
      : "";

  return errors;
}

function isFormInvalid(errors: IAnnexDataFormErrors): boolean {
  return errors.title !== "" || errors.content !== "" || errors.category !== "";
}

export default AnnexDataAddEdit;
